// Firebase Auth, loaded on demand.
//
// The Firebase client SDK (app + auth: ~130 KB raw, ~39 KB gzip) used to be
// imported statically by AuthProvider and ~90 components, so every page
// downloaded and parsed it before it could become interactive. Nothing may
// import "firebase/auth" statically any more (eslint enforces it; type-only
// imports are fine): the SDK is fetched the first time something needs it -
// a returning visitor's session check right after hydration, a logged-out
// visitor's first idle moment, the sign-in modal, or an authed request - and
// every caller shares the one instance.
//
// For Authorization headers use getIdToken() / requireIdToken() rather than
// getAuth().currentUser.getIdToken(). They also wait for Firebase to restore
// the session, so a request made while the page is still loading no longer
// goes out without a token (currentUser is null until that restore ends).
import type { Auth, User } from "firebase/auth";
import { setIdTokenGetter } from "@jammable/clientapi/src/shared";

export type FirebaseAuthModule = typeof import("firebase/auth");

let apiPromise: Promise<{ auth: Auth; fb: FirebaseAuthModule }> | null = null;
let loadedAuth: Auth | null = null;
let stateReady = false;
const waiting = new Set<(auth: Auth) => void>();

/** Loads the SDK (once) and returns the Auth instance plus the module, for
 * calling sign-in functions: `const { auth, fb } = await loadAuthApi();
 * fb.signInWithPopup(auth, new fb.GoogleAuthProvider())`. Client only. */
export function loadAuthApi(): Promise<{ auth: Auth; fb: FirebaseAuthModule }> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Firebase Auth is client-only"));
  }
  if (!apiPromise) {
    apiPromise = Promise.all([import("firebase/auth"), import("./firebase")])
      .then(([fb, app]) => {
        const auth = fb.getAuth(app.default);
        loadedAuth = auth;
        auth
          .authStateReady()
          .then(() => {
            stateReady = true;
          })
          .catch(() => {});
        waiting.forEach((cb) => cb(auth));
        waiting.clear();
        return { auth, fb };
      })
      .catch((err) => {
        // A failed chunk fetch (flaky network, a deploy swapping chunks)
        // must not poison every later sign-in or authed request.
        apiPromise = null;
        throw err;
      });
  }
  return apiPromise;
}

export function loadAuth(): Promise<Auth> {
  return loadAuthApi().then(({ auth }) => auth);
}

/** The Auth instance if something has already loaded it; never loads it. */
export function getLoadedAuth(): Auth | null {
  return loadedAuth;
}

/** Calls `cb` with the Auth instance as soon as anything loads it (now, if
 * it already has), without triggering the load. Returns a canceller. */
export function whenAuthLoaded(cb: (auth: Auth) => void): () => void {
  if (loadedAuth) {
    cb(loadedAuth);
    return () => {};
  }
  waiting.add(cb);
  return () => {
    waiting.delete(cb);
  };
}

/** The signed-in Firebase user, after Firebase has restored the session.
 * null when signed out, and always null on the server. */
export async function getCurrentUser(): Promise<User | null> {
  if (typeof window === "undefined") return null;
  const auth = await loadAuth();
  await auth.authStateReady();
  return auth.currentUser;
}

/** ID token for an Authorization header, or null when signed out. */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = await getCurrentUser();
  return user ? user.getIdToken(forceRefresh) : null;
}

/** ID token, or throws when signed out - for callers that used
 * `auth.currentUser.getIdToken()` and relied on it throwing. */
export async function requireIdToken(): Promise<string> {
  const user = await getCurrentUser();
  if (!user) throw new Error("User not authenticated.");
  return user.getIdToken();
}

/** Synchronous best answer to "is someone signed in?": Firebase's, once it
 * has loaded and restored the session; until then `fallback`, which should
 * be AuthProvider's user (the cached profile on a return visit). */
export function isSignedIn(fallback: unknown): boolean {
  if (loadedAuth && stateReady) return !!loadedAuth.currentUser;
  return !!fallback;
}

/** auth.onAuthStateChanged, loading the SDK first - or, with
 * `{ load: false }`, subscribing only once something else has loaded it
 * (nobody can sign in before that). Returns an unsubscriber that is safe to
 * call before the SDK has arrived. */
export function onAuthStateChanged(
  cb: (user: User | null) => void,
  { load = true }: { load?: boolean } = {},
): () => void {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;
  const subscribe = (auth: Auth) => {
    if (!cancelled) unsubscribe = auth.onAuthStateChanged(cb);
  };
  let stopWaiting = () => {};
  if (load) loadAuth().then(subscribe, () => {});
  else stopWaiting = whenAuthLoaded(subscribe);
  return () => {
    cancelled = true;
    stopWaiting();
    unsubscribe?.();
  };
}

export async function signOut(): Promise<void> {
  const auth = await loadAuth();
  await auth.signOut();
}

// packages/ui's API helpers (getAuthHeaders) build their headers from this.
setIdTokenGetter(() => getIdToken());
